// eslint-disable-next-line max-classes-per-file
import $ from 'jquery';
import { debounce } from 'lodash';

/**
 * @desc Basic building blocks of pagination component.
 */
class AtomTemplates {
  /**
   * @desc Generic element template.
   * @param {Object} options
   * @param {string} options.elName
   * @param {boolean} options.isSelfClosing
   * @param {string} options.elContent
   * @param {string} options.elClass
   * @param {string} options.elAttr
   * @returns {string}
   */
  static el({
    elName = '', isSelfClosing = false, elContent = '', elClass = '', elAttr = '',
  }) {
    const regularElement = `
      <${elName} ${elClass ? `class="${elClass}"` : ''} ${elAttr} >${elContent}</${elName}>
    `;

    const selfClosingElement = `
      <${elName} ${elClass ? `class="${elClass}"` : ''} ${elAttr} />
    `;

    return isSelfClosing ? selfClosingElement : regularElement;
  }

  /**
   * @desc Pagination list template.
   * @param {Object} options
   * @param {string} options.listContent
   * @param {string} options.listClass
   * @param {string} options.listAttr
   * @returns {string}
   */
  static list({ listContent = '', listClass = '', listAttr = '' } = {}) {
    return `
      <ul class="pagination-list ${listClass} " ${listAttr} >${listContent}</ul>
    `;
  }

  /**
   * @desc Pagination item template.
   * @param {Object} options
   * @param {string} options.itemContent
   * @param {string} options.itemClass
   * @param {string} options.itemAttr
   * @returns {string}
   */
  static item({ itemContent = '', itemClass = '', itemAttr = '' } = {}) {
    return `
      <li class="pagination-list__item ${itemClass} " ${itemAttr} >${itemContent}</li>
    `;
  }

  /**
   * @desc Pagination link template.
   * @param {Object} options
   * @param {string} options.linkContent
   * @param {string} options.linkHref
   * @param {string} options.linkClass
   * @param {string} options.linkAttr
   * @returns {string}
   */
  static link({
    linkContent = '', linkHref = '#', linkClass = '', linkAttr = '',
  } = {}) {
    return `
      <a class="pagination-list__item-link ${linkClass} " href="${linkHref}" ${linkAttr} >${linkContent}</a>
    `;
  }

  /**
   * @desc Pagination icon template.
   * @param {Object} options
   * @param {string} options.xlinkId
   * @param {string} options.svgClass
   * @param {string} options.svgAttr
   * @returns {string}
   */
  static icon({ xlinkId = '', svgClass = '', svgAttr = '' } = {}) {
    return `
      <svg class="pagination-list__icon ${svgClass} " ${svgAttr} >
        <use xlink:href="#${xlinkId}"/>
      </svg>
    `;
  }

  /**
   * @desc Pagination dots template.
   * @returns {string}
   */
  static dots() {
    return `
      <ul class="pagination-dots">
        <li class="pagination-dots__dot"></li>
        <li class="pagination-dots__dot"></li>
        <li class="pagination-dots__dot"></li>
      </ul>
    `;
  }
}

/**
 * @desc Composite building blocks of pagination component.
 */
class MoleculeTemplates {
  /**
   * @desc Page switcher.
   * @param {Object} options
   * @param {('back'|'forward')} options.direction
   * @param {string} options.pageHref
   * @param {boolean} options.isActive
   * @return {string}
   */
  static pageSwitcher({
    direction = 'back', pageHref = '#', isActive = false, isReversed = false,
  } = {}) {
    const options = {
      back: {
        iconId: 'pagination-previous-page',
        classActive: 'pagination-list__item--move-back',
        classDisabled: 'pagination-list__item--move-back pagination-list__item--disabled',
        ariaLabel: 'Previous page',
      },
      forward: {
        iconId: 'pagination-next-page',
        classActive: 'pagination-list__item--move-forward',
        classDisabled: 'pagination-list__item--move-forward pagination-list__item--disabled',
        ariaLabel: 'Next page',
      },
    };

    const icon = AtomTemplates.icon({
      xlinkId: options[direction].iconId,
      svgClass: isReversed ? 'rotate180' : '',
    });

    return AtomTemplates.item({
      itemClass: isActive
        ? options[direction].classActive
        : options[direction].classDisabled,
      itemContent: isActive
        ? AtomTemplates.link({
          linkAttr: `aria-label="${options[direction].ariaLabel}"`,
          linkContent: icon,
          linkHref: pageHref,
        })
        : AtomTemplates.el({
          elName: 'span',
          elClass: 'pagination-list__item-link',
          elContent: icon,
        }),
    });
  }

  /**
   * @desc Shows whether we have more or less pages to display or not.
   * @param {Object} options
   * @param {('less'|'more')} options.has
   * @returns {string}
   */
  static indicator({ has = 'less' }) {
    const options = {
      less: {
        class: 'pagination-list__item--has-less',
      },
      more: {
        class: 'pagination-list__item--has-more',
      },
    };

    return AtomTemplates.item({
      itemClass: `pagination-list__item--indicator ${options[has].class}`,
      itemContent: AtomTemplates.dots(),
    });
  }

  /**
   * @desc Pagination 'page number'.
   * @returns {string}
   */
  static pageNumber({
    isActivePage = false, isFirstPage = false, isLastPage = false, pageHref = '#', number = 1,
  } = {}) {
    let itemClass = 'pagination-list__item--page-number';

    if (isActivePage) {
      itemClass += ' pagination-list__item--active';
    }

    if (isFirstPage) {
      itemClass += ' pagination-list__item--page-number-first';
    }

    if (isLastPage) {
      itemClass += ' pagination-list__item--page-number-last';
    }

    if (number >= 100 && number < 1000) {
      itemClass += ' pagination-list__item--font-sm';
    } else if (number >= 1000 && number < 10000) {
      itemClass += ' pagination-list__item--font-xs';
    } else if (number >= 10000) {
      itemClass += ' pagination-list__item--font-xxs';
    }

    return AtomTemplates.item({
      itemClass,
      itemContent: AtomTemplates.link({
        linkHref: pageHref,
        linkContent: number,
      }),
    });
  }
}

export default class Pagination {
  /**
   * @param {string} paginationSelector
   * @param {string} dataDirKey
   */
  constructor(paginationSelector, dataDirKey = 'dir') {
    this.$pagination = $(paginationSelector);
    this.isRtl = String(this.$pagination.data(dataDirKey)).toLocaleLowerCase() === 'rtl';

    this.init();
  }

  /**
   * @desc Returns widths of pagination items (page numbers, arrow buttons, dots) in pixels.
   */
  static getItemsWidthsInPixels() {
    return {
      pageNumberWidth: 38,
      pageSwitcherWidth: 32,
      dotsIndicatorWidth: 16,
    };
  }

  /**
   * @desc Init module.
   */
  init() {
    this.isInitialized = true;

    if (!this.$pagination.length) {
      this.isInitialized = false;

      return;
    }

    this.render();

    $(window).on('resize', debounce(() => this.render(), 200));
  }

  /**
   * @desc Render pagination list.
   */
  render() {
    const itemsWidths = Pagination.getItemsWidthsInPixels();

    const {
      totalPagesQuantity,
      pageNumbers,
    } = this.calculatePages(itemsWidths.pageSwitcherWidth * 2);

    const isMustShowLeftDots = pageNumbers[0] > 1;
    const isMustShowRightDots = pageNumbers[pageNumbers.length - 1] < totalPagesQuantity;

    let calculatedData;

    if (
      (isMustShowLeftDots && !isMustShowRightDots)
      || (!isMustShowLeftDots && isMustShowRightDots)
    ) {
      calculatedData = this
        .calculatePages((itemsWidths.pageSwitcherWidth * 2) + itemsWidths.dotsIndicatorWidth);
    } else if (isMustShowLeftDots && isMustShowRightDots) {
      calculatedData = this
        .calculatePages((itemsWidths.pageSwitcherWidth * 2) + (itemsWidths.dotsIndicatorWidth * 2));
    } else {
      calculatedData = this.calculatePages(itemsWidths.pageSwitcherWidth * 2);
    }

    const pageSize = this.getPageSize();

    this.$pagination.html(
      $.parseHTML(
        AtomTemplates.list({
          listContent: Pagination.getItems({ ...calculatedData, ...{ pageSize }, isReversed: this.isRtl }).join(''),
        }),
      ),
    );
  }

  /**
   * @desc Returns array of pagination items (side arrows, dots indicators, page numbers).
   * @param {Object} options
   * @param {number} options.totalPagesQuantity
   * @param {number} options.currentPageNumber
   * @param {number} options.paginationCapacity
   * @param {number[]} options.pageNumbers
   * @return {string[]}
   */
  static getItems({
    totalPagesQuantity = 0, currentPageNumber = 0, paginationCapacity = 0,
    pageNumbers = [], isReversed = false, pageSize,
  } = {}) {
    const searchParams = new URLSearchParams(window.location.search);

    /**
     * @desc Create an array of page numbers controls.
     */
    const paginationItems = Array
      .from(
        { length: paginationCapacity },
        (_, i) => {
          searchParams.set('page', pageNumbers[i]);

          if (pageSize) {
            searchParams.set('pagesize', pageSize);
          }

          return MoleculeTemplates.pageNumber({
            isActivePage: pageNumbers[i] === currentPageNumber,
            isFirstPage: i === 0,
            isLastPage: i === paginationCapacity - 1,
            pageHref: `${window.location.pathname}?${searchParams.toString()}`,
            number: pageNumbers[i],
          });
        },
      );

    /**
     * @desc If necessary, add three points to the left.
     */
    if (pageNumbers[0] > 1) {
      paginationItems.unshift(MoleculeTemplates.indicator({ has: 'less' }));
    }

    /**
     * @desc Adds "switcher to the previous page" button.
     */
    searchParams.set('page', currentPageNumber - 1);
    paginationItems.unshift(MoleculeTemplates.pageSwitcher({
      direction: 'back',
      isActive: currentPageNumber > 1,
      pageHref: `${window.location.pathname}?${searchParams.toString()}`,
      isReversed,
    }));

    /**
     * @desc If necessary, add three points to the right.
     */
    if (pageNumbers[pageNumbers.length - 1] < totalPagesQuantity) {
      paginationItems.push(MoleculeTemplates.indicator({ has: 'more' }));
    }

    /**
     * @desc Adds "switcher to the next page" button.
     */
    searchParams.set('page', currentPageNumber + 1);
    paginationItems.push(MoleculeTemplates.pageSwitcher({
      direction: 'forward',
      isActive: currentPageNumber < totalPagesQuantity,
      pageHref: `${window.location.pathname}?${searchParams.toString()}`,
      isReversed,
    }));

    return paginationItems;
  }

  /**
   * @desc Calculate pagination.
   */
  calculatePages(occupiedWith) {
    const currentPageNumber = this.getCurrentPage();
    const totalPagesQuantity = this.getTotalPagesCount();
    const availableWidth = this.$pagination.width();

    /**
     * @desc Calculate how many page numbers can be displayed in pagination.
     * @type {number}
     */
    let paginationCapacity = Math
      .floor((availableWidth - occupiedWith) / Pagination.getItemsWidthsInPixels().pageNumberWidth);

    /**
     * @desc If we are able to display more pages than we have, then pick the total page number.
     */
    if (paginationCapacity > totalPagesQuantity) {
      paginationCapacity = totalPagesQuantity;
    }

    /**
     * @desc Calculate position reaching which we have to shift the pagination.
     * @type {number}
     */
    const rubiconPosition = Math.round(paginationCapacity / 2);

    /**
     * @desc Shows whether we reached rubicon position or not.
     * @type {boolean}
     */
    const isShouldShift = currentPageNumber >= rubiconPosition;

    /**
     * @desc Shows whether we can shift the pagination or not.
     * (is there at least one element to display beyond currently displayed pages)
     * @type {boolean}
     */
    const isCanShift = (
      currentPageNumber + Math.trunc(paginationCapacity / 2) + 1
    ) <= totalPagesQuantity;

    let startPageNumber;

    if (!isShouldShift) {
      startPageNumber = 1;
    } else if (isShouldShift && !isCanShift) {
      startPageNumber = totalPagesQuantity - paginationCapacity + 1;
    } else {
      startPageNumber = (currentPageNumber - Math.trunc(paginationCapacity / 2)) || 1;
    }

    const pageNumbers = Array.from({ length: paginationCapacity }, (_, i) => startPageNumber + i);

    return {
      totalPagesQuantity,
      currentPageNumber,
      paginationCapacity,
      pageNumbers,
    };
  }

  getCurrentPage() {
    const currentPageXlg = this.$pagination.data('current-page-xlg');

    if (currentPageXlg && window.matchMedia('(min-width: 1024px)').matches) {
      return currentPageXlg;
    }

    const currentPageLg = this.$pagination.data('current-page-lg');

    if (currentPageLg && window.matchMedia('(min-width: 768px)').matches) {
      return currentPageLg;
    }

    const currentPageXxs = this.$pagination.data('current-page-xxs');

    if (currentPageXxs) {
      return currentPageXxs;
    }

    return this.$pagination.data('current-page');
  }

  getPageSize() {
    const pageSizeXlg = this.$pagination.data('page-size-xlg');

    if (pageSizeXlg && window.matchMedia('(min-width: 1024px)').matches) {
      return pageSizeXlg;
    }

    const pageSizeLg = this.$pagination.data('page-size-lg');

    if (pageSizeLg && window.matchMedia('(min-width: 768px)').matches) {
      return pageSizeLg;
    }

    const pageSizeXxs = this.$pagination.data('page-size-xxs');

    if (pageSizeXxs) {
      return pageSizeXxs;
    }

    return this.$pagination.data('page-size');
  }

  getTotalPagesCount() {
    const totalPagesXlg = this.$pagination.data('total-pages-xlg');

    if (totalPagesXlg && window.matchMedia('(min-width: 1024px)').matches) {
      return totalPagesXlg;
    }

    const totalPagesLg = this.$pagination.data('total-pages-lg');

    if (totalPagesLg && window.matchMedia('(min-width: 768px)').matches) {
      return totalPagesLg;
    }

    const totalPagesXxs = this.$pagination.data('total-pages-xxs');

    if (totalPagesXxs) {
      return totalPagesXxs;
    }

    return this.$pagination.data('total-pages');
  }
}
